import { MinusIcon } from '@common_components/icons/minus.icon';
import { PlusIcon } from '@common_components/icons/plus.icon';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { EQUIPMENT_COLUMN } from 'constants/import.constants';

import { Button, CommonGlobalService, DownloadIcon, RootState, UploadIcon } from '@umanforge/common';

import { SearchInput } from 'components/inputs/search_input.component';
import { SeeMore } from 'components/inputs/see_more.component';
import { HomeLeftBar } from 'components/menu/home_left_bar.component';
import { HomeTopBar } from 'components/menu/home_top_bar.component';

import { GlobalService } from 'services/global.service';
import { NavigationService } from 'services/navigation.service';
import { useEquipments } from '../hooks/equipments.hook';
import { useDispatch, useSelector } from 'react-redux';
import { EquipmentsList } from '../components/equipments.list';
import { ExportModal } from 'features/import_export/components/modal_export.component';
import { EquipmentService } from 'services/equipment.service';
import { ImportModal } from 'features/import_export/components/modal_import.component';
import { ImportHelperModal } from 'features/import_export/components/import_helper_modal.component';

export function EquipmentsView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );

  const {
    clients, setClients,
    sites, setSites,
    sectors,
    equipments,
    filterName, setFilterName,
    filterClients,
    filterSites,
    filterSectors,
    seeMoreClients, setSeeMoreClients,
    isLoading,
    isFilterEnd,
    onChangeFilteredSectors,
    onChangeFilteredSites,
    onChangeFilteredClients,
    isOpenExportModal, openExportModal, closeExportModal,
    isOpenImportModal, openImportModal, closeImportModal
  } = useEquipments();


  return (
    <>
      <HomeLeftBar items={ NavigationService.getNavBarItems( token, dispatch ) } needConfirmation={ false } />
      <div>
        <div>
          <HomeTopBar title={ t( 'common:equipment' ) } needConfirmationToNav={ false }/>
        </div>
        <div className='flex h-full gap-1 mx-2'>

          <div className='flex flex-col gap-10 pt-10 w-[250px] h-full'>
            {
              me && ( CommonGlobalService.isClientAdmin( me ) || CommonGlobalService.isSiteAdmin( me ) ) &&
                <div className='flex flex-col w-[250px]'>
                  <Button
                    label={ t( 'new_equipment' ) }
                    className='w-full m-1 bg-[#0BB173] hover:bg-[#088D5C] hover:ring-1 hover:ring-[#088D5C] text-sm font-medium text-white'
                    icon={ <PlusIcon className='w-[24px] h-[24px] p-0.5 fill-white' /> }
                    onClick={ () => { NavigationService.toNewEquipment( navigate, false ); } }
                  />
                  {
                    CommonGlobalService.isClientAdmin( me ) ?
                      <div className='flex'>
                        <div className='flex items-center w-full'>
                          <Button
                            label={ t( 'common:export' ) }
                            className='w-full m-1 bg-[#0BB173] hover:bg-[#088D5C] hover:ring-1 hover:ring-[#088D5C] text-sm font-medium text-white'
                            icon={ <DownloadIcon className='w-[24px] h-[24px] p-0.5 text-white'/> }
                            onClick={ openExportModal }
                          />
                        </div>
                        <div className='flex items-center'>
                          <Button
                            label={ t( 'common:import' ) }
                            className='w-full m-1 bg-[#0BB173] hover:bg-[#088D5C] hover:ring-1 hover:ring-[#088D5C] text-sm font-medium text-white'
                            icon={ <UploadIcon className='w-[24px] h-[24px] p-0.5 text-white'/> }
                            onClick={ openImportModal }
                          />
                          <ImportHelperModal
                            title={ t( 'import_help_title' ) }
                            text={
                              <div className='space-y-2 text-sm text-gray-700'>
                                <p className='font-semibold text-gray-800'>{ t( 'template_columns_intro' ) }</p>
                                <ul className='list-disc pl-5 space-y-1'>
                                  {
                                    Object.values( EQUIPMENT_COLUMN )
                                      .map( column => {
                                        return ( <li key={ column }> { t( column ).trim() } </li> );
                                      } )
                                  }
                                </ul>
                              </div>
                            }
                            api={ () => { return EquipmentService.downloadEquipmentsTemplate( token, dispatch ); } }
                          />
                        </div>
                      </div>
                      :
                      null
                  }
                </div>
            }
            {
              clients.length != 0 &&
                <div className='relative border p-1 rounded-lg text-left h-fit w-full'>
                  <div className='absolute w-fit px-1.5 -top-3.5 left-3 bg-white font-bold'>{ t( 'Client' ) }</div>
                  <div className='px-4 py-2 text-sm'>
                    {
                      <div>
                        {
                          clients.slice( 0, seeMoreClients ? clients.length : 5 ).map( client => {
                            const sitesOfClient = sites.filter( site => { return site.clientId === client.id; } );
                            return (
                              <div key={ client.id } className='flex flex-col mb-1'>
                                <div className='flex gap-2 items-center select-none'>
                                  <input
                                    type='checkbox'
                                    id={ client.id }
                                    className='h-fit cursor-pointer'
                                    checked={ filterClients.find( clientId => { return clientId === client.id; } ) != null }
                                    onChange={ e => { onChangeFilteredClients( client.id, ( e.target as HTMLInputElement ).checked ); } }
                                  />
                                  <label htmlFor={ client.id } className='font-medium cursor-pointer truncate'>{ client.name }</label>
                                  <div
                                    style={{ display: me?.isSuperAdmin == true ? '' : 'none' }}
                                    onClick={ () => {
                                      client.isCollapsed = !client.isCollapsed;
                                      setClients( structuredClone( clients ) );
                                    } }
                                  >
                                    {
                                      sitesOfClient && sitesOfClient.length != 0 &&
                                        <div className='w-[18px] h-[18px] bg-gray-200 p-1 ml-1 rounded-full'>
                                          {
                                            client.isCollapsed === true || GlobalService.clientOrChildrensIsFilter( client, { clients: filterClients, sites: filterSites, sectors: [] } ) ?
                                              <MinusIcon className='w-[12px] -mt-[2px] -ml-[1px]'/>
                                              :
                                              <PlusIcon className='w-[10px]'/>
                                          }
                                        </div>
                                    }
                                  </div>
                                </div>
                                <div className='ml-1.5'>
                                  {
                                    sitesOfClient && (
                                      client.isCollapsed ||
                                      filterClients.includes( client.id ) ||
                                      sitesOfClient.find( site => {
                                        return filterSites.includes( site.id ) || sectors.find( sector => {
                                          return sector.siteId == site.id && filterSectors.includes( sector.id );
                                        } );
                                      } )
                                    ) != null &&
                                      sitesOfClient.map( site => {
                                        const sectorOfSite = sectors.filter( sector => { return sector.siteId === site.id; } );
                                        return (
                                          <div key={ site.id } className='flex flex-col mb-1'>
                                            <div className='flex gap-2 items-center select-none'>
                                              <div className='flex gap-2 items-center select-none'>
                                                <input
                                                  type='checkbox'
                                                  id={ site.id }
                                                  className='h-fit cursor-pointer'
                                                  checked={ filterSites.find( siteId => { return siteId === site.id; } ) != null }
                                                  onChange={ e => { onChangeFilteredSites( site.id, ( e.target as HTMLInputElement ).checked ); } }
                                                />
                                                <label htmlFor={ site.id } className='font-medium cursor-pointer truncate'>{ site.name }</label>
                                                <div
                                                  style={{ display: me?.isSuperAdmin == true ? '' : 'none' }}
                                                  onClick={ () => {
                                                    site.isCollapsed = !site.isCollapsed;
                                                    setSites( structuredClone( sites ) );
                                                  } }
                                                >
                                                  {
                                                    sitesOfClient && sitesOfClient.length != 0 &&
                                                      <div className='w-[18px] h-[18px] bg-gray-200 p-1 ml-1 rounded-full'>
                                                        {
                                                          site.isCollapsed === true || GlobalService.clientOrChildrensIsFilter( client, { clients: filterClients, sites: filterSites, sectors: [] } ) ?
                                                            <MinusIcon className='w-[12px] -mt-[2px] -ml-[1px]'/>
                                                            :
                                                            <PlusIcon className='w-[10px]'/>
                                                        }
                                                      </div>
                                                  }
                                                </div>
                                              </div>
                                            </div>
                                            <div className='ml-1.5'>
                                              {
                                                sectorOfSite && ( site.isCollapsed || filterSites.includes( site.id ) || sectorOfSite.find( sector => { return filterSectors.includes( sector.id ); } ) ) != null ?
                                                  sectorOfSite.map( sector => {
                                                    return (
                                                      <div key={ sector.id } className='flex flex-col mb-1'>
                                                        <div className='flex gap-2 items-center select-none'>
                                                          <input
                                                            type='checkbox'
                                                            id={ sector.id }
                                                            className='h-fit cursor-pointer'
                                                            checked={ filterSectors.find( sectorId => { return sectorId === sector.id; } ) != null }
                                                            onChange={ e => { onChangeFilteredSectors( sector.id, ( e.target as HTMLInputElement ).checked ); } }
                                                          />
                                                          <label htmlFor={ sector.id } className='cursor-pointer truncate text-xs'>{ sector.name }</label>
                                                        </div>
                                                      </div>
                                                    );
                                                  } )
                                                  :
                                                  null
                                              }
                                            </div>
                                          </div>
                                        );
                                      } )
                                  }
                                </div>
                              </div>
                            );
                          } )
                        }
                        {
                          clients.length > 5 &&
                            <SeeMore
                              isSeeMore={ seeMoreClients }
                              onClick={ () => { setSeeMoreClients( !seeMoreClients ); } }
                              t={t}
                            />
                        }
                      </div>
                    }
                  </div>
                </div>
            }
          </div>

          <div className='flex flex-col w-full h-full mx-2 items-center'>
            {
              isFilterEnd &&
                <div className='flex w-1/2 min-w-[400px] gap-2 justify-center items-center'>
                  <SearchInput
                    value={ filterName }
                    onSearched={ value => { setFilterName( value ); } }
                    t={ t }
                  />
                </div>
            }
            {
              <EquipmentsList
                clients={clients}
                sites={sites}
                sectors={sectors}
                equipments={equipments}
                isLoading={isLoading}
                t={t}
              />
            }

            <ExportModal
              title={ t( 'translation:export_equipments' ) }
              isOpen={ isOpenExportModal }
              onClose={ closeExportModal }
              exportFunction={ EquipmentService.exportEquipmentsOfClient }
              showSiteFilter={ true }
              showSectorFilter={ true }
            />
            <ImportModal
              title={ t( 'translation:import_equipments' ) }
              isOpen={ isOpenImportModal }
              onClose={ closeImportModal }
              importFunction={ EquipmentService.importEquipmentsOnClient }
            />
          </div>

        </div>
      </div>
    </>
  );

}